import type { Attributes, AttributeValue } from '@opentelemetry/api';
import type { ExportResult } from '@opentelemetry/core';
import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base';

/** Drops the query string and fragment, which can carry tokens, ids or other personal data. */
export function scrubUrl(url: string): string {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

// Attributes (old and stable HTTP semantic conventions, plus document-load's) that hold a URL.
const URL_ATTRIBUTES = ['http.url', 'url.full', 'http.target', 'http.referrer', 'document.referrer'];
// Attributes that exist only to carry the query string or fragment.
const DROPPED_ATTRIBUTES = ['url.query', 'url.fragment', 'http.query', 'http.fragment'];

/** Returns a copy of the span attributes with URLs reduced to origin + path and query/fragment attributes removed. */
export function scrubSpanAttributes(attributes: Attributes): Attributes {
  const scrubbed: Record<string, AttributeValue | undefined> = { ...attributes };
  for (const key of URL_ATTRIBUTES) {
    const value = scrubbed[key];
    if (typeof value === 'string') scrubbed[key] = scrubUrl(value);
  }
  for (const key of DROPPED_ATTRIBUTES) {
    delete scrubbed[key];
  }
  return scrubbed;
}

/**
 * Exports through another exporter after scrubbing each span's URL attributes. Done at export time, once every
 * attribute (including those the instrumentations add when a request finishes) is final.
 */
export class ScrubbingSpanExporter implements SpanExporter {
  constructor(private readonly inner: SpanExporter) {}

  export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
    for (const span of spans) {
      const scrubbed = scrubSpanAttributes(span.attributes);
      for (const key of Object.keys(span.attributes)) {
        if (!(key in scrubbed)) delete span.attributes[key];
      }
      Object.assign(span.attributes, scrubbed);
    }
    this.inner.export(spans, resultCallback);
  }

  shutdown(): Promise<void> {
    return this.inner.shutdown();
  }

  forceFlush(): Promise<void> {
    return this.inner.forceFlush?.() ?? Promise.resolve();
  }
}
